import { Box, Flex, SimpleGrid, Table, Text } from "@mantine/core";
import dayjs from "dayjs";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import useSwr from "swr";
import { ARApi } from "../../apis";
import { DateRangeChip, ListStates, type DateRangeValue } from "../list";
import { currencyFormat } from "../../utils";
import { dateFormat } from "../../utils/date";
import classes from "../../pages/customer-billing/BillingDetails.module.css";

interface Props {
  businessId: string;
  /** Earliest open invoice payment/confirmed date — widens default range when older than 3 months. */
  earliestOpenDate?: string | null;
}

interface StatementTransaction {
  transactionDate: string;
  transactionType: string;
  transactionNumber: string;
  description: string;
  debitAmount: number;
  creditAmount: number;
  balance: number;
}

interface StatementData {
  openingBalance: number;
  totalInvoiced: number;
  totalPaid: number;
  closingBalance: number;
  transactions: StatementTransaction[];
}

function defaultRange(earliestOpenDate?: string | null): DateRangeValue {
  const to = dayjs().format("YYYY-MM-DD");
  let from = dayjs().subtract(3, "month").startOf("day");
  if (earliestOpenDate) {
    const earliest = dayjs(earliestOpenDate).startOf("day");
    if (earliest.isValid() && earliest.isBefore(from)) {
      from = earliest;
    }
  }
  return [from.format("YYYY-MM-DD"), to];
}

export function ARStatementTable({ businessId, earliestOpenDate }: Props) {
  const { t } = useTranslation("billing");
  const [range, setRange] = useState<DateRangeValue>(() => defaultRange(earliestOpenDate));

  const periodFrom = range[0] || dayjs().subtract(3, "month").format("YYYY-MM-DD");
  const periodTo = range[1] || dayjs().format("YYYY-MM-DD");

  const { data, error, isLoading, mutate } = useSwr<StatementData>(
    businessId ? `ar.statement.${businessId}.${periodFrom}.${periodTo}` : null,
    async () => await ARApi.getStatement(businessId, { periodFrom, periodTo, limit: 100 }),
    { revalidateOnFocus: false },
  );

  const TYPE_LABELS: Record<string, string> = useMemo(
    () => ({
      INVOICE: t("ar.typeInvoice"),
      PAYMENT: t("ar.typePayment"),
    }),
    [t],
  );

  const summaryCards = [
    { label: t("ar.openingBalance"), value: currencyFormat(data?.openingBalance ?? 0, { withSymbol: false }) },
    { label: t("ar.totalInvoiced"), value: currencyFormat(data?.totalInvoiced ?? 0, { withSymbol: false }) },
    { label: t("ar.totalPaid"), value: currencyFormat(data?.totalPaid ?? 0, { withSymbol: false }) },
    { label: t("ar.closingBalance"), value: currencyFormat(data?.closingBalance ?? 0, { withSymbol: false }) },
  ];

  const rows = data?.transactions ?? [];

  return (
    <Box>
      <Flex justify="space-between" align="center" mb="md" wrap="wrap" gap="sm">
        <Text fw={600} fz={15} c="gray.9">
          {t("tab.statement")}
        </Text>
        <DateRangeChip
          label={t("detail.statementPeriod")}
          value={range}
          onChange={setRange}
          size="compact"
        />
      </Flex>

      <SimpleGrid cols={{ base: 2, sm: 4 }} mb="md">
        {summaryCards.map((card) => (
          <Box key={card.label} className={classes.contactLine}>
            <Text className={classes.contactLabel}>{card.label}</Text>
            <Text className={classes.contactValue} style={{ fontVariantNumeric: "tabular-nums" }}>
              {card.value}
            </Text>
          </Box>
        ))}
      </SimpleGrid>

      <Box className={classes.tblWrap}>
        {rows.length === 0 ? (
          <ListStates
            loading={isLoading}
            error={error}
            onRetry={() => mutate()}
            empty
            columns={7}
            emptyTitle={t("ar.noTransactions")}
          />
        ) : (
        <Table className={classes.tbl} horizontalSpacing={0} verticalSpacing={0}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t("ar.colDate")}</Table.Th>
              <Table.Th>{t("ar.colType")}</Table.Th>
              <Table.Th>{t("ar.colNumber")}</Table.Th>
              <Table.Th>{t("ar.colDesc")}</Table.Th>
              <Table.Th className={classes.colAmt}>{t("ar.colDebit")}</Table.Th>
              <Table.Th className={classes.colAmt}>{t("ar.colCredit")}</Table.Th>
              <Table.Th className={classes.colAmt}>{t("ar.colBalance")}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map((tx, i) => (
                <Table.Tr key={`${tx.transactionNumber}-${i}`}>
                  <Table.Td className={classes.colMuted}>
                    {tx.transactionDate ? dateFormat(tx.transactionDate) : "-"}
                  </Table.Td>
                  <Table.Td>{TYPE_LABELS[tx.transactionType] ?? tx.transactionType}</Table.Td>
                  <Table.Td>
                    <Text ff="monospace" fz={14} fw={500}>
                      {tx.transactionNumber || "-"}
                    </Text>
                  </Table.Td>
                  <Table.Td>{tx.description || "-"}</Table.Td>
                  <Table.Td className={classes.colAmt}>
                    {tx.debitAmount
                      ? currencyFormat(tx.debitAmount, { withSymbol: false })
                      : "-"}
                  </Table.Td>
                  <Table.Td className={`${classes.colAmt} ${classes.amtPaid}`}>
                    {tx.creditAmount
                      ? currencyFormat(tx.creditAmount, { withSymbol: false })
                      : "-"}
                  </Table.Td>
                  <Table.Td className={classes.colAmt}>
                    {currencyFormat(tx.balance, { withSymbol: false })}
                  </Table.Td>
                </Table.Tr>
              ))}
          </Table.Tbody>
        </Table>
        )}
      </Box>
    </Box>
  );
}
